import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Button, Icon } from "@qota/ui";
import { supportsPasskeys } from "../lib/auth-client";
import { CONTENT_BOTTOM } from "../lib/insets";
import { useSessionState } from "../lib/session-context";
import { signInWithPasskey } from "../lib/passkey";

/**
 * Schermata 13 — Benvenuto.
 *
 * Il testo è quello dei mockup, parola per parola: è copy scritto, non
 * segnaposto.
 */
export function WelcomeScreen() {
  const navigate = useNavigate();
  const { refresh } = useSessionState();
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [passkeyError, setPasskeyError] = useState<string | null>(null);
  const canUsePasskeys = supportsPasskeys();

  async function enterWithPasskey() {
    setPasskeyBusy(true);
    setPasskeyError(null);

    const outcome = await signInWithPasskey();

    setPasskeyBusy(false);

    // Annullare il dialogo di sistema è una scelta, non un errore da segnalare.
    if (outcome.cancelled) return;
    if (!outcome.ok) {
      setPasskeyError(outcome.error ?? "Accesso con passkey non riuscito.");
      return;
    }

    // Siamo già su "/": navigare qui non rimonterebbe nulla e la schermata
    // resterebbe questa, autenticata ma senza saperlo. È `refresh` a rileggere
    // la sessione, e lo smistamento in ingresso porta dove serve.
    await refresh();
  }

  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        background: "var(--surface-canvas)",
      }}
    >
      <div
        style={{
          flex: 1,
          width: "100%",
          maxWidth: 430,
          margin: "0 auto",
          padding: `max(var(--space-8), calc(env(safe-area-inset-top, 0px) + var(--space-4))) var(--space-5) ${CONTENT_BOTTOM}`,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}
        >
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: "var(--radius-lg)",
              background: "var(--surface-accent)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "var(--font-display)",
              fontSize: 30,
              fontWeight: 600,
              letterSpacing: "-0.03em",
              color: "var(--text-on-lime)",
              marginBottom: "var(--space-6)",
            }}
            aria-hidden="true"
          >
            Q
          </div>

          <h1
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 30,
              lineHeight: "36px",
              fontWeight: 600,
              letterSpacing: "-0.03em",
              margin: 0,
            }}
          >
            Qota
          </h1>

          <p
            style={{
              fontSize: 17,
              lineHeight: "24px",
              fontWeight: 600,
              letterSpacing: "-0.2px",
              margin: "var(--space-3) 0 0",
              textWrap: "pretty",
            }}
          >
            Le spese di gruppo, senza il foglio Excel.
          </p>

          <p
            style={{
              fontSize: 14,
              lineHeight: "21px",
              color: "var(--ink-600)",
              margin: "var(--space-3) 0 0",
              textWrap: "pretty",
            }}
          >
            Aggiungi una spesa, scegli come dividerla, e Qota ti dice chi deve cosa a chi. Fino a
            quando il conto è pari.
          </p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
          <Button
            variant="accent"
            size="lg"
            full
            iconLeft={<Icon name="mail" size={18} />}
            onClick={() => void navigate({ to: "/auth" })}
          >
            Continua con l'email
          </Button>

          <Button
            variant="secondary"
            size="lg"
            full
            iconLeft={<Icon name="phone" size={18} />}
            onClick={() => void navigate({ to: "/auth" })}
          >
            Continua con il telefono
          </Button>

          {/*
            Mostrato solo dove le passkey funzionano: altrove sarebbe un
            pulsante che porta soltanto a un errore.
          */}
          {canUsePasskeys && (
            <Button
              variant="secondary"
              size="lg"
              full
              disabled={passkeyBusy}
              iconLeft={<Icon name="circle-check" size={18} />}
              onClick={() => void enterWithPasskey()}
            >
              {passkeyBusy ? "Attendo il dispositivo…" : "Entra con una passkey"}
            </Button>
          )}

          {passkeyError && (
            <div
              role="alert"
              style={{
                fontSize: 12.5,
                fontWeight: 700,
                color: "var(--coral-500)",
                textAlign: "center",
              }}
            >
              {passkeyError}
            </div>
          )}

          <Button
            variant="ghost"
            size="lg"
            full
            iconLeft={<Icon name="ticket" size={18} />}
            onClick={() => void navigate({ to: "/invito" })}
          >
            Ho un codice o un link d'invito
          </Button>

          <p
            style={{
              fontSize: 11,
              lineHeight: "16px",
              color: "var(--text-tertiary)",
              textAlign: "center",
              margin: "var(--space-3) 0 0",
              textWrap: "pretty",
            }}
          >
            Continuando accetti i termini e la privacy. Qota non chiede mai i dati della tua banca.
          </p>
        </div>
      </div>
    </div>
  );
}
